// Offline Context - Manages offline state and sync for Tasks module
import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import axios from 'axios'
import { toast } from 'sonner'
import offlineDB from '../services/offlineDB'

const OfflineContext = createContext(null)

const API = `${process.env.REACT_APP_BACKEND_URL}/api`

// Sync status enum
export const SYNC_STATUS = {
  SYNCED: 'synced',
  PENDING: 'pending',
  SYNCING: 'syncing',
  FAILED: 'failed'
}

// Evidence status enum
export const EVIDENCE_STATUS = {
  SYNCED: 'synced',
  LOCAL_ONLY: 'local_only',
  SYNCING: 'syncing',
  FAILED: 'failed'
}

// Max retry attempts for sync
const MAX_RETRIES = 3

export function OfflineProvider({ children }) {
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  const [isDBReady, setIsDBReady] = useState(false)
  const [isSyncing, setIsSyncing] = useState(false)
  const [syncStats, setSyncStats] = useState({ pendingTasks: 0, pendingEvidences: 0 })
  const [lastSyncTime, setLastSyncTime] = useState(null)
  
  const syncInProgress = useRef(false)
  const syncTimeoutRef = useRef(null)

  // Initialize IndexedDB
  useEffect(() => {
    const initDB = async () => {
      try {
        await offlineDB.init()
        setIsDBReady(true)
        console.log('[Offline] IndexedDB initialized')
        
        // Load last sync time
        const lastSync = await offlineDB.getMeta('lastSyncTime')
        if (lastSync) setLastSyncTime(new Date(lastSync))
        
        // Update stats
        await updateSyncStats()
      } catch (error) {
        console.error('[Offline] Failed to init IndexedDB:', error)
      }
    }
    initDB()
  }, [])

  // Network status listeners
  useEffect(() => {
    const handleOnline = () => {
      console.log('[Offline] Network: ONLINE')
      setIsOnline(true)
      // Trigger sync after a short delay to ensure connection is stable
      if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current)
      syncTimeoutRef.current = setTimeout(() => {
        syncAll()
      }, 2000)
    }

    const handleOffline = () => {
      console.log('[Offline] Network: OFFLINE')
      setIsOnline(false)
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current)
      }
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
      if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current)
    }
  }, [])

  // Update sync stats
  const updateSyncStats = useCallback(async () => {
    if (!isDBReady) return
    try {
      const stats = await offlineDB.getStats()
      setSyncStats({
        pendingTasks: stats.pendingTasks,
        pendingEvidences: stats.pendingEvidences,
        syncQueueSize: stats.syncQueueSize
      })
    } catch (error) {
      console.error('[Offline] Failed to update stats:', error)
    }
  }, [isDBReady])

  // ==========================================
  // TASK OPERATIONS (with offline support)
  // ==========================================

  // Cache tasks from server
  const cacheTasks = useCallback(async (tasks) => {
    if (!isDBReady) return
    try {
      await offlineDB.saveTasks(tasks)
      await offlineDB.setMeta('lastSyncTime', new Date().toISOString())
      setLastSyncTime(new Date())
      console.log('[Offline] Cached', tasks.length, 'tasks')
    } catch (error) {
      console.error('[Offline] Failed to cache tasks:', error)
    }
  }, [isDBReady])

  // Get cached tasks (for offline use)
  const getCachedTasks = useCallback(async () => {
    if (!isDBReady) return []
    try {
      return await offlineDB.getAllTasks()
    } catch (error) {
      console.error('[Offline] Failed to get cached tasks:', error)
      return []
    }
  }, [isDBReady])

  // Create task (works offline)
  const createTaskOffline = useCallback(async (taskData) => {
    if (!isDBReady) return null
    
    // Generate a temporary local ID
    const localId = `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    
    const newTask = {
      id: localId,
      ...taskData,
      status: 'Pendiente',
      created_at: new Date().toISOString(),
      sync_status: SYNC_STATUS.PENDING,
      last_local_update_at: new Date().toISOString(),
      is_local: true
    }
    
    try {
      await offlineDB.saveTask(newTask)
      
      // Add to sync queue
      await offlineDB.addToSyncQueue({
        type: 'CREATE_TASK',
        local_id: localId,
        data: taskData
      })
      
      await updateSyncStats()
      console.log('[Offline] Task created locally:', localId)
      
      return newTask
    } catch (error) {
      console.error('[Offline] Failed to create task offline:', error)
      return null
    }
  }, [isDBReady, updateSyncStats])

  // Update task (works offline)
  const updateTaskOffline = useCallback(async (taskId, updateData) => {
    if (!isDBReady) return null
    
    try {
      const existingTask = await offlineDB.getTask(taskId)
      if (!existingTask) {
        console.error('[Offline] Task not found for update:', taskId)
        return null
      }
      
      const updatedTask = {
        ...existingTask,
        ...updateData,
        sync_status: SYNC_STATUS.PENDING,
        last_local_update_at: new Date().toISOString()
      }
      
      await offlineDB.saveTask(updatedTask)
      
      // Add to sync queue (if not a local-only task)
      if (!existingTask.is_local) {
        await offlineDB.addToSyncQueue({
          type: 'UPDATE_TASK',
          task_id: taskId,
          data: updateData,
          original_update_at: existingTask.last_local_update_at
        })
      }
      
      await updateSyncStats()
      console.log('[Offline] Task updated locally:', taskId)
      
      return updatedTask
    } catch (error) {
      console.error('[Offline] Failed to update task offline:', error)
      return null
    }
  }, [isDBReady, updateSyncStats])

  // ==========================================
  // EVIDENCE OPERATIONS (with offline support)
  // ==========================================

  // Save evidence locally with image blob
  const saveEvidenceOffline = useCallback(async (taskId, file) => {
    if (!isDBReady) return null
    
    try {
      // Read file as array buffer
      const arrayBuffer = await file.arrayBuffer()
      const localId = `local_ev_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      
      const evidence = {
        local_id: localId,
        task_id: taskId,
        filename: file.name,
        file_type: file.type,
        file_size: file.size,
        blob_data: arrayBuffer,
        evidence_status: EVIDENCE_STATUS.LOCAL_ONLY,
        created_at: new Date().toISOString(),
        retry_count: 0
      }
      
      await offlineDB.saveEvidence(evidence)
      
      // Add to sync queue
      await offlineDB.addToSyncQueue({
        type: 'UPLOAD_EVIDENCE',
        local_id: localId,
        task_id: taskId,
        filename: file.name
      })
      
      await updateSyncStats()
      console.log('[Offline] Evidence saved locally:', localId)
      
      return {
        id: localId,
        task_id: taskId,
        filename: file.name,
        file_size: file.size,
        evidence_status: EVIDENCE_STATUS.LOCAL_ONLY,
        uploaded_at: new Date().toISOString(),
        // Create local URL for preview
        local_url: URL.createObjectURL(new Blob([arrayBuffer], { type: file.type }))
      }
    } catch (error) {
      console.error('[Offline] Failed to save evidence offline:', error)
      return null
    }
  }, [isDBReady, updateSyncStats])

  // Get evidences for a task (local + remote)
  const getLocalEvidences = useCallback(async (taskId) => {
    if (!isDBReady) return []
    try {
      const evidences = await offlineDB.getEvidencesByTask(taskId)
      return evidences.map(ev => ({
        ...ev,
        id: ev.local_id,
        local_url: ev.blob_data 
          ? URL.createObjectURL(new Blob([ev.blob_data], { type: ev.file_type }))
          : null
      }))
    } catch (error) {
      console.error('[Offline] Failed to get local evidences:', error)
      return []
    }
  }, [isDBReady])

  // ==========================================
  // SYNC OPERATIONS
  // ==========================================

  // Sync a single evidence
  const syncEvidence = async (queueItem) => {
    try {
      const evidence = await offlineDB.getEvidence(queueItem.local_id)
      if (!evidence) {
        console.log('[Sync] Evidence not found, removing from queue:', queueItem.local_id)
        return { success: true, remove: true }
      }
      
      // Check if task has been synced (has real server ID)
      let realTaskId = queueItem.task_id
      if (realTaskId.startsWith('local_')) {
        // Task hasn't been synced yet, skip for now
        console.log('[Sync] Task not synced yet, deferring evidence:', queueItem.local_id)
        return { success: false, defer: true }
      }
      
      // Create FormData with the blob
      const formData = new FormData()
      const blob = new Blob([evidence.blob_data], { type: evidence.file_type })
      formData.append('file', blob, evidence.filename)
      
      const response = await axios.post(`${API}/tasks/${realTaskId}/evidences`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      
      // Update local evidence with server data
      await offlineDB.updateEvidenceStatus(queueItem.local_id, EVIDENCE_STATUS.SYNCED, {
        server_id: response.data.id,
        file_url: response.data.file_url
      })
      
      console.log('[Sync] Evidence synced:', queueItem.local_id, '->', response.data.id)
      return { success: true }
    } catch (error) {
      console.error('[Sync] Evidence sync failed:', queueItem.local_id, error)
      
      // Increment retry count
      const evidence = await offlineDB.getEvidence(queueItem.local_id)
      if (evidence) {
        const newRetryCount = (evidence.retry_count || 0) + 1
        if (newRetryCount >= MAX_RETRIES) {
          await offlineDB.updateEvidenceStatus(queueItem.local_id, EVIDENCE_STATUS.FAILED)
          return { success: false, maxRetries: true }
        }
        evidence.retry_count = newRetryCount
        await offlineDB.saveEvidence(evidence)
      }
      
      return { success: false, error: error.message }
    }
  }

  // Sync a single task
  const syncTask = async (queueItem) => {
    try {
      if (queueItem.type === 'CREATE_TASK') {
        const localTask = await offlineDB.getTask(queueItem.local_id)
        if (!localTask) {
          console.log('[Sync] Local task not found, removing from queue:', queueItem.local_id)
          return { success: true, remove: true }
        }
        
        // Create on server
        const response = await axios.post(`${API}/tasks`, queueItem.data)
        const serverTask = response.data
        
        // Update local task with server ID
        await offlineDB.deleteTask(queueItem.local_id)
        await offlineDB.saveTask({
          ...serverTask,
          sync_status: SYNC_STATUS.SYNCED,
          last_synced_at: new Date().toISOString()
        })
        
        // Update any pending evidences that reference this local task
        const pendingEvidences = await offlineDB.getEvidencesByTask(queueItem.local_id)
        for (const ev of pendingEvidences) {
          ev.task_id = serverTask.id
          await offlineDB.saveEvidence(ev)
        }
        
        console.log('[Sync] Task created:', queueItem.local_id, '->', serverTask.id)
        return { success: true, mappedId: { local: queueItem.local_id, server: serverTask.id } }
        
      } else if (queueItem.type === 'UPDATE_TASK') {
        // Update on server using "last write wins" strategy
        const response = await axios.put(`${API}/tasks/${queueItem.task_id}`, queueItem.data)
        
        // Update local task
        await offlineDB.updateTaskSyncStatus(queueItem.task_id, SYNC_STATUS.SYNCED, response.data)
        
        console.log('[Sync] Task updated:', queueItem.task_id)
        return { success: true }
      }
      
      return { success: false, error: 'Unknown sync type' }
    } catch (error) {
      console.error('[Sync] Task sync failed:', queueItem, error)
      return { success: false, error: error.message }
    }
  }

  // Main sync function
  const syncAll = useCallback(async () => {
    if (!isDBReady || !isOnline || syncInProgress.current) {
      console.log('[Sync] Skip sync - DB:', isDBReady, 'Online:', isOnline, 'InProgress:', syncInProgress.current)
      return
    }
    
    syncInProgress.current = true
    setIsSyncing(true)
    
    console.log('[Sync] Starting sync...')
    
    try {
      const queue = await offlineDB.getSyncQueue()
      console.log('[Sync] Queue size:', queue.length)
      
      if (queue.length === 0) {
        console.log('[Sync] Nothing to sync')
        setIsSyncing(false)
        syncInProgress.current = false
        return
      }
      
      // Track ID mappings for local -> server IDs
      const idMappings = {}
      let successCount = 0
      let failCount = 0
      
      // Process queue in order
      // First: CREATE_TASK items (to get server IDs)
      // Then: UPDATE_TASK items
      // Finally: UPLOAD_EVIDENCE items
      const sortedQueue = [...queue].sort((a, b) => {
        const order = { 'CREATE_TASK': 1, 'UPDATE_TASK': 2, 'UPLOAD_EVIDENCE': 3 }
        return (order[a.type] || 99) - (order[b.type] || 99)
      })
      
      for (const item of sortedQueue) {
        let result
        
        // Update task_id if we have a mapping
        if (item.task_id && idMappings[item.task_id]) {
          item.task_id = idMappings[item.task_id]
        }
        
        if (item.type === 'CREATE_TASK' || item.type === 'UPDATE_TASK') {
          result = await syncTask(item)
          if (result.mappedId) {
            idMappings[result.mappedId.local] = result.mappedId.server
          }
        } else if (item.type === 'UPLOAD_EVIDENCE') {
          result = await syncEvidence(item)
          if (result.defer) {
            continue // Skip for now, will retry later
          }
        }
        
        if (result?.success || result?.remove || result?.maxRetries) {
          await offlineDB.removeSyncQueueItem(item.id)
          if (result.success) successCount++
        } else {
          failCount++
          // Update retry count
          await offlineDB.updateSyncQueueItem(item.id, {
            retries: (item.retries || 0) + 1,
            status: 'failed',
            last_error: result?.error
          })
        }
      }
      
      // Update last sync time
      await offlineDB.setMeta('lastSyncTime', new Date().toISOString())
      setLastSyncTime(new Date())
      
      // Update stats
      await updateSyncStats()
      
      // Show result notification
      if (successCount > 0 || failCount > 0) {
        if (failCount === 0) {
          toast.success(`Sincronizado: ${successCount} cambio(s)`)
        } else {
          toast.warning(`Sincronizado: ${successCount} OK, ${failCount} fallido(s)`)
        }
      }
      
      console.log('[Sync] Complete. Success:', successCount, 'Failed:', failCount)
      
    } catch (error) {
      console.error('[Sync] Sync failed:', error)
      toast.error('Error al sincronizar')
    } finally {
      setIsSyncing(false)
      syncInProgress.current = false
    }
  }, [isDBReady, isOnline, updateSyncStats])

  // Retry failed evidence
  const retryEvidence = useCallback(async (localId) => {
    if (!isDBReady || !isOnline) return false
    
    try {
      const evidence = await offlineDB.getEvidence(localId)
      if (!evidence || evidence.evidence_status !== EVIDENCE_STATUS.FAILED) {
        return false
      }
      
      // Reset status and retry count
      evidence.evidence_status = EVIDENCE_STATUS.LOCAL_ONLY
      evidence.retry_count = 0
      await offlineDB.saveEvidence(evidence)
      
      // Add back to sync queue
      await offlineDB.addToSyncQueue({
        type: 'UPLOAD_EVIDENCE',
        local_id: localId,
        task_id: evidence.task_id,
        filename: evidence.filename
      })
      
      // Trigger sync
      await syncAll()
      
      return true
    } catch (error) {
      console.error('[Offline] Failed to retry evidence:', error)
      return false
    }
  }, [isDBReady, isOnline, syncAll])

  // Force sync
  const forceSync = useCallback(() => {
    if (isOnline) {
      syncAll()
    } else {
      toast.error('Sin conexión. Sincronización pendiente.')
    }
  }, [isOnline, syncAll])

  const value = {
    // State
    isOnline,
    isDBReady,
    isSyncing,
    syncStats,
    lastSyncTime,
    
    // Task operations
    cacheTasks,
    getCachedTasks,
    createTaskOffline,
    updateTaskOffline,
    
    // Evidence operations
    saveEvidenceOffline,
    getLocalEvidences,
    retryEvidence,
    
    // Sync operations
    syncAll,
    forceSync,
    updateSyncStats
  }

  return (
    <OfflineContext.Provider value={value}>
      {children}
    </OfflineContext.Provider>
  )
}

export function useOffline() {
  const context = useContext(OfflineContext)
  if (!context) {
    throw new Error('useOffline must be used within an OfflineProvider')
  }
  return context
}

export default OfflineContext
